/**
 * Pure adapters that turn an acquisition (Lumio) Ad-Service campaign into the input for
 * creating an affiliate campaign, plus the helpers the "Available Lumio campaigns" section
 * needs. No React / axios / window here, so it unit-tests in isolation.
 *
 * Two deliberate choices:
 *  - The imported name uses `resolveCampaignName`'s DEFAULT language, not the owner's current
 *    UI locale, so the persisted affiliate campaign name does not depend on who imported it.
 *  - Dates render as UTC calendar days (YYYY-MM-DD), matching the affiliate campaign's string
 *    date fields and the acquisition area's UTC convention.
 */
import { resolveCampaignName, type AdCampaignSummary } from '../acquisition/api/casino-ad-service';
import type { AffiliateCampaign, CampaignCreateInput } from './types';

/**
 * Normalise the brand's public domain to an absolute origin, or '' when absent.
 * Mirrors the creatives page (`src/nova/pages/creatives/index.tsx`): a bare `brand.com`
 * becomes `https://brand.com`, an already-absolute value is left untouched.
 */
export const normalizeBrandUrl = (domain: string | undefined): string => {
  if (!domain) return '';
  return /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
};

/**
 * Resolve the affiliate landing URL an imported campaign should point at. Affiliates must
 * receive an ABSOLUTE, shareable URL, so a bare path from the Ad Service is joined onto the
 * brand's own domain. A relative/empty path with no brand domain cannot be made absolute and
 * resolves to '' (the owner edits it later).
 */
export const resolveLandingUrl = (adUrl: string | undefined, brandBaseUrl: string): string => {
  if (adUrl) {
    if (/^https?:\/\//i.test(adUrl)) return adUrl; // already absolute (has host)
    if (/^\/\//.test(adUrl)) return `https:${adUrl}`; // protocol-relative //host/path
  }
  if (!brandBaseUrl) return ''; // nothing to anchor a relative/empty path onto
  if (!adUrl) return brandBaseUrl; // no path → brand root
  return `${brandBaseUrl.replace(/\/+$/, '')}/${adUrl.replace(/^\/+/, '')}`;
};

/** Epoch SECONDS → `YYYY-MM-DD` (UTC calendar day). */
export const epochSecToYmd = (epochSeconds: number): string =>
  new Date(epochSeconds * 1000).toISOString().slice(0, 10);

/**
 * Map an acquisition campaign to the affiliate-campaign create input. `external_ref` carries
 * the acquisition `campaign_id` so the imported campaign is dedup-able and reconcilable back to
 * its source (and, later, to the spark-creatives campaign authority). `available_to_all` is
 * true because "Make available to Affiliates" broadcasts to the whole program; the owner can
 * narrow it afterwards via the normal campaign edit. An open-ended campaign omits `end_date`.
 */
export const toAffiliateCampaignInput = (ad: AdCampaignSummary, brandBaseUrl: string): CampaignCreateInput => ({
  name: resolveCampaignName(ad.campaign_name),
  landing_url: resolveLandingUrl(ad.landing_page_url, brandBaseUrl),
  start_date: epochSecToYmd(ad.start_date),
  ...(ad.end_date == null ? {} : { end_date: epochSecToYmd(ad.end_date) }),
  available_to_all: true,
  external_ref: ad.campaign_id,
});

/** How a Lumio (ad-service) campaign relates to the affiliate program right now. */
export type LumioRowStatus = 'new' | 'changed';

/** A Lumio campaign the owner can act on: import a 'new' one, or update a 'changed' one. */
export interface LumioRow {
  campaign: AdCampaignSummary;
  status: LumioRowStatus;
  /** The affiliate campaign id to update (present only for 'changed'). */
  existingId?: string;
}

const normField = (value?: string): string => (value ?? '').trim();

/**
 * True when the ad-service source no longer matches the affiliate campaign it was imported into,
 * on the fields the import maps (name, landing URL, start/end dates). We have no import-time
 * snapshot, so this compares source-now vs affiliate-now: a manual edit to the imported campaign
 * also counts as a difference (an "Update" would overwrite it with the source values).
 */
export const lumioCampaignDiffers = (
  ad: AdCampaignSummary,
  existing: AffiliateCampaign,
  brandBaseUrl: string,
): boolean => {
  const mapped = toAffiliateCampaignInput(ad, brandBaseUrl);
  return (
    normField(mapped.name) !== normField(existing.name) ||
    normField(mapped.landing_url) !== normField(existing.landing_url) ||
    normField(mapped.start_date) !== normField(existing.start_date) ||
    normField(mapped.end_date) !== normField(existing.end_date)
  );
};

/**
 * The Lumio rows the owner should see: a campaign NOT yet imported ('new'), or one imported but
 * whose source now differs ('changed'). An identical, already-imported campaign is omitted
 * (hidden). `sessionAppliedIds` folds in campaign_ids created OR updated this session but not yet
 * reflected in the reloaded affiliate list, so a just-actioned row disappears immediately (and a
 * second click can't duplicate it) across the reload gap.
 */
export const visibleLumioRows = (
  adCampaigns: readonly AdCampaignSummary[],
  affiliateCampaigns: readonly AffiliateCampaign[],
  sessionAppliedIds: ReadonlySet<string>,
  brandBaseUrl: string,
): LumioRow[] => {
  const byRef = new Map<string, AffiliateCampaign>();
  for (const campaign of affiliateCampaigns) {
    if (campaign.external_ref) byRef.set(campaign.external_ref, campaign);
  }
  const rows: LumioRow[] = [];
  for (const ad of adCampaigns) {
    // Just created/updated this session (reload pending) → treat as reconciled, hide it.
    if (sessionAppliedIds.has(ad.campaign_id)) continue;
    const existing = byRef.get(ad.campaign_id);
    if (!existing) {
      rows.push({ campaign: ad, status: 'new' });
    } else if (lumioCampaignDiffers(ad, existing, brandBaseUrl)) {
      rows.push({ campaign: ad, status: 'changed', existingId: existing.id });
    }
    // imported and identical → omitted
  }
  return rows;
};
